import { useState } from "react";
import { AlertTriangle, Loader2, ScanBarcode } from "lucide-react";
import * as api from "../../api/client";
import { Alert, Chip, Filters, List, Pager, Stat, Stats } from "./ui";
import { code, pct, TONE, usePaged, when } from "./lib";

const FILTERS = [
  ["", "Hammasi"],
  ["unrated", "Baholanmagan"],
  ["wrong", "Xato tanilgan"],
  ["parts", "Qism xatosi"],
  ["failed", "Bajarilmagan"],
];

const PAGE = 24;

/** Fotodan tasvir: barcha mijozlarning vazifalari, narx va ekspert baholari. */
export default function VisualTab({ days, refresh, preset = "" }) {
  const [kind, setKind] = useState(preset);
  const [stats, setStats] = useState(null);

  const list = usePaged(
    (page) =>
      api.reviewVisuals({ days, kind, limit: PAGE, offset: page * PAGE }).then((r) => {
        setStats(r.stats);
        return r;
      }),
    [kind, days],
    refresh,
  );

  const jobs = stats?.jobs;
  const fb = stats?.feedback;

  return (
    <div className="space-y-5">
      <Alert>{list.error}</Alert>

      {stats && (
        <Stats>
          <Stat label="Tayyor" value={jobs.done} hint={`${jobs.failed} ta xato`} />
          <Stat label="Bugun" value={jobs.today} hint={`$${jobs.cost_today.toFixed(2)}`} />
          <Stat
            label="O'rtacha narx"
            value={`$${jobs.avg_cost.toFixed(3)}`}
            hint={`${(jobs.avg_ms / 1000).toFixed(0)} s · ${jobs.avg_attempts.toFixed(1)} urinish`}
          />
          <Stat label="Baholangan" value={fb.rated} />
          <Stat
            label="To'g'ri tanilgan"
            value={fb.identity_ok}
            hint={fb.rated ? `${pct(fb.identity_ok, fb.rated)}%` : ""}
            tone={fb.rated ? "text-emerald-300" : ""}
          />
          <Stat label="Qism xatosi bor" value={fb.with_wrong_parts} tone={fb.with_wrong_parts ? "text-rose-300" : ""} />
        </Stats>
      )}

      <Filters items={FILTERS} value={kind} onChange={setKind} />

      <List loading={list.loading} items={list.items}>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {list.items.map((v) => (
            <VisualTile key={v.id} v={v} />
          ))}
        </div>
      </List>

      <Pager page={list.page} setPage={list.setPage} full={list.items.length === PAGE} />
    </div>
  );
}

function Verdict({ fb }) {
  if (!fb) return <Chip cls={TONE.dim}>baholanmagan</Chip>;
  if (fb.identity_ok === false) return <Chip cls={TONE.bad}>xato tanilgan</Chip>;
  if (fb.wrong_parts.length) return <Chip cls={TONE.bad}>{fb.wrong_parts.length} qism xato</Chip>;
  return <Chip cls={TONE.ok}>to'g'ri</Chip>;
}

function VisualTile({ v }) {
  const name = v.product?.name || v.product_hint || "Foto tahlili";
  const top = v.product?.codes?.[0]?.code;
  return (
    <a
      href={`/?visual=${v.id}`}
      className="group block overflow-hidden rounded-xl border border-sky-800/40 bg-[#030f24]/70 transition-colors hover:border-sky-500/60"
    >
      <div className="flex aspect-[4/3] items-center justify-center bg-[#041228]">
        {v.status === "done" && v.files ? (
          <img src={api.sized(v.files.image, 320)} alt="" loading="lazy" className="h-full w-full bg-white object-contain" />
        ) : v.status === "done" ? (
          <ScanBarcode className="h-6 w-6 text-sky-300/70" />
        ) : v.status === "failed" ? (
          <AlertTriangle className="h-6 w-6 text-rose-300/80" />
        ) : (
          <Loader2 className="h-6 w-6 animate-spin text-sky-300/70" />
        )}
      </div>
      <div className="space-y-1 p-2.5">
        <div className="truncate text-xs font-medium text-white">{name}</div>
        <div className="flex flex-wrap items-center gap-1.5">
          {top && <Chip>{code(top)}</Chip>}
          {v.status === "failed" ? <Chip cls={TONE.bad}>{v.error?.code || "xato"}</Chip> : <Verdict fb={v.feedback} />}
        </div>
        <div className="font-mono text-[10px] text-sky-400/50">
          {when(v.created_at)}
          {v.usage && ` · $${v.usage.cost_usd.toFixed(3)}`}
          {v.client !== "local" && ` · ${v.client}`}
        </div>
      </div>
    </a>
  );
}
